import type { Lyric } from '@lyricstream/shared';

/** Numéro affiché du chant dans son recueil (position, à défaut l'ancien identifiant). */
const numberOf = (lyric: Lyric): string => String(lyric.position ?? lyric.idLegacy ?? '');

/**
 * Filtre texte de la page recueil. La recherche Meilisearch (figée sur le recueil) fournit `matchedIds` : titre,
 * artistes et paroles, insensible aux accents et aux fautes. Le numéro du chant, absent de l'index texte, reste
 * cherché ici (début du numéro). Tant que la recherche serveur n'a pas répondu, ou si elle échoue (`matchedIds`
 * nul), le filtre local sur le titre prend le relais.
 */
export function filterByQuery(
  list: Lyric[],
  query: string,
  matchedIds: ReadonlySet<string> | null,
): Lyric[] {
  const q = query.trim().toLowerCase();
  if (!q) return list;

  return list.filter(
    (lyric) =>
      (matchedIds ? matchedIds.has(lyric.id) : lyric.title.toLowerCase().includes(q)) ||
      numberOf(lyric).startsWith(q),
  );
}

/**
 * Trie par pertinence : l'ordre des ids renvoyés par la recherche serveur (le `Set` garde l'ordre d'insertion). Les
 * chants absents de la recherche (trouvés par leur numéro) passent après, par numéro croissant.
 */
export function sortByRelevance(list: Lyric[], matchedIds: ReadonlySet<string>): Lyric[] {
  const rank = new Map<string, number>([...matchedIds].map((id, index) => [id, index]));
  const rankOf = (lyric: Lyric): number => rank.get(lyric.id) ?? Number.POSITIVE_INFINITY;
  const position = (lyric: Lyric): number => lyric.position ?? lyric.idLegacy ?? 0;

  return [...list].sort((a, b) => {
    const diff = rankOf(a) - rankOf(b);
    return Number.isNaN(diff) || diff === 0 ? position(a) - position(b) : diff;
  });
}
